import { useState, useRef, RefObject } from "react";
import { User } from "../../../types/types";
import { useUserSearch } from "../../../api/v1/queries/users.query";

export interface Mention {
  id: string;
  user: {
    id: number;
    firstName: string;
    lastName: string;
  };
  position: number;
}

interface UseMentionsProps {
  contentRef: RefObject<HTMLDivElement>;
  hiddenInputRef: RefObject<HTMLInputElement>;
}

export const useMentions = ({ contentRef, hiddenInputRef }: UseMentionsProps) => {
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const lastAtPositionRef = useRef<number>(-1);

  const { data: searchResults, isLoading: isSearchLoading } = useUserSearch({
    limit: 10,
    offset: 0,
    searchValue: searchTerm,
  });

  const users = searchResults?.pages[0]?.items ?? [];

  const verifyMentions = () => {
    if (!contentRef.current) return;

    const currentMentions = mentions.filter((mention) => {
      const element = document.getElementById(mention.id);
      return element && contentRef.current?.contains(element);
    });

    if (currentMentions.length !== mentions.length) {
      setMentions(currentMentions);
    }
  };

  const updateHiddenInput = () => {
    if (!contentRef.current || !hiddenInputRef.current) return;

    let text = contentRef.current.textContent || "";
    let hiddenText = text;

    const sortedMentions = [...mentions].sort((a, b) => b.position - a.position);

    for (const mention of sortedMentions) {
      const mentionElement = document.getElementById(mention.id);
      if (mentionElement && contentRef.current?.contains(mentionElement)) {
        const mentionText = mentionElement.textContent || "";
        hiddenText = hiddenText.replace(mentionText, `{{User:${mention.user.id}}}`);
      }
    }

    hiddenInputRef.current.value = hiddenText;
  };

  const getCaretPosition = (element: HTMLElement): number => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    return preCaretRange.toString().length;
  };

  const handleMentionInput = () => {
    const selection = window.getSelection();
    if (!selection || !contentRef.current) return;

    verifyMentions();

    const cursorPosition = getCaretPosition(contentRef.current);

    let currentPosition = 0;
    let foundAt = -1;

    const nodes = Array.from(contentRef.current.childNodes);
    for (const node of nodes) {
      const nodeLength = node.textContent?.length || 0;

      if (currentPosition + nodeLength >= cursorPosition) {
        const textBeforeCursor = node.textContent?.slice(0, cursorPosition - currentPosition) || "";
        const lastAtIndex = textBeforeCursor.lastIndexOf("@");

        if (lastAtIndex !== -1) {
          foundAt = currentPosition + lastAtIndex;
          const searchText = textBeforeCursor.slice(lastAtIndex + 1);

          if (!searchText.includes(" ")) {
            setSearchTerm(searchText);
            setShowMentions(true);
            lastAtPositionRef.current = foundAt;
            return;
          }
        }
        break;
      }

      currentPosition += nodeLength;
    }

    setShowMentions(false);
  };

  const handleUserSelect = (user: User) => {
    if (!contentRef.current) return;

    const atPosition = lastAtPositionRef.current;
    if (atPosition === -1) return;

    const mentionId = `mention-${Date.now()}`;

    const mentionSpan = document.createElement("span");
    mentionSpan.id = mentionId;
    mentionSpan.className = "mention";
    mentionSpan.contentEditable = "false";
    mentionSpan.textContent = `@${user.firstName} ${user.lastName}`;

    const nodes = Array.from(contentRef.current.childNodes);
    let currentPosition = 0;
    let insertAfterNode: Node | null = null;
    let remainingText = "";

    for (const node of nodes) {
      const nodeLength = node.textContent?.length || 0;

      if (currentPosition + nodeLength > atPosition) {
        const textNode = node as Text;
        const splitPosition = atPosition - currentPosition;

        const beforeText = textNode.textContent?.slice(0, splitPosition) || "";
        const afterText = textNode.textContent?.slice(splitPosition) || "";

        remainingText = afterText.replace(/^@\w*/, "");

        textNode.textContent = beforeText;
        insertAfterNode = textNode;
        break;
      }

      currentPosition += nodeLength;
      insertAfterNode = node;
    }

    if (insertAfterNode) {
      if (insertAfterNode.nextSibling) {
        contentRef.current.insertBefore(mentionSpan, insertAfterNode.nextSibling);
        contentRef.current.insertBefore(document.createTextNode(remainingText + " "), mentionSpan.nextSibling);
      } else {
        contentRef.current.appendChild(mentionSpan);
        contentRef.current.appendChild(document.createTextNode(remainingText + " "));
      }
    } else {
      contentRef.current.appendChild(mentionSpan);
      contentRef.current.appendChild(document.createTextNode(" "));
    }

    setMentions((prev) => [
      ...prev,
      {
        id: mentionId,
        user: {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
        },
        position: atPosition,
      },
    ]);

    setShowMentions(false);
    setSearchTerm("");
    lastAtPositionRef.current = -1;

    contentRef.current.focus();
    const selection = window.getSelection();
    const range = document.createRange();

    const lastNode = contentRef.current.lastChild;
    if (lastNode && lastNode.nodeType === Node.TEXT_NODE) {
      range.setStart(lastNode, lastNode.textContent?.length || 0);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  const resetMentions = () => {
    setMentions([]);
    lastAtPositionRef.current = -1;
  };

  return {
    mentions,
    searchTerm,
    showMentions,
    users,
    isSearchLoading,
    handleMentionInput,
    handleUserSelect,
    verifyMentions,
    updateHiddenInput,
    resetMentions,
  };
};
